// src/modules/app-views/invoices/pages/Init/modals/ExportXmlModal/index.jsx
// *******************************************************************************
// EXPORT XML MODAL
// *******************************************************************************
// Modal for batch exporting invoice XMLs as a ZIP file.
// User selects a month and document type (NFe/NFCe/Both).
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { ConfigProvider } from 'antd';
import { FileZipOutlined } from '@ant-design/icons';
import { createRoot } from 'react-dom/client';
import dayjs from 'dayjs';
import GenericModal from 'components/core/GenericModal';
import useZustand from 'hooks/useZustand';
import { darkTheme, lightTheme } from 'configs/appEngine';
import invoicesService from 'modules/app-views/invoices/service';
import appService from 'services/appService';
import createExportXmlStep from './Step1';

// *******************************************************************************
// THEME WRAPPER (required for modals with createRoot)
// *******************************************************************************
const ThemeWrapper = ({ children }) => {
  const theme = useZustand('theme', 'currentTheme');
  const config = theme === 'dark' ? darkTheme : lightTheme;
  return <ConfigProvider theme={config}>{children}</ConfigProvider>;
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ExportXmlModal = ({ show, setShow, afterClose, company }) => {
  const steps = useMemo(() => [createExportXmlStep({ company })], [company]);

  const handleFinish = async ({ form, setLoading }) => {
    const values = form.getFieldsValue();
    const monthDayjs = values.month;
    const documentType = values.documentType || 'all';

    if (!monthDayjs) {
      appService.message({
        flag: 'error',
        text: 'select_month_required',
      });
      return false;
    }

    // Convert dayjs to YYYY-MM string format expected by backend
    const month = dayjs.isDayjs(monthDayjs) ? monthDayjs.format('YYYY-MM') : monthDayjs;

    // Get companyId for multi-company filtering
    const companyId = company?._id || company?.entityId;
    // Get company CNPJ for filename
    const companyCnpj = company?.cnpj?.value || company?.documento?.cnpj?.value;

    const result = await invoicesService.exportXmlBatch({
      month,
      documentType,
      companyId,
      companyCnpj,
    });

    return result;
  };

  return (
    <GenericModal
      show={show}
      setShow={setShow}
      icon={<FileZipOutlined />}
      title="export_xml"
      steps={steps}
      onFinish={handleFinish}
      afterClose={afterClose}
      width={440}
      maskClosable={true}
      initialValues={{ documentType: 'all' }}
    />
  );
};

// *******************************************************************************
// SHOW MODAL FUNCTION (creates modal in React root)
// *******************************************************************************
const showExportXmlModal = ({ onClose, company } = {}) => {
  const container = document.createElement('div');
  container.id = 'export-xml-modal-container';
  document.body.appendChild(container);

  const root = createRoot(container);

  let isModalOpen = true;

  const renderModal = () => {
    root.render(
      <ThemeWrapper>
        <ExportXmlModal
          show={isModalOpen}
          setShow={handleClose}
          afterClose={handleAfterClose}
          company={company}
        />
      </ThemeWrapper>
    );
  };

  const handleClose = () => {
    isModalOpen = false;
    renderModal();
  };

  const handleAfterClose = () => {
    setTimeout(() => {
      root.unmount();
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
      onClose?.();
    }, 0);
  };

  renderModal();
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default showExportXmlModal;
export { ExportXmlModal };
